import { useState } from "react";

import { Box } from "@chakra-ui/react";
import { IApiErrorData } from "api/axios/types";
import {
  IBvnVerificationPayload,
  IBvnVerificationResponse,
} from "api/compliance/types";
import { ReactComponent as IconCopy } from "assets/icons/copy.svg";
import { ReactComponent as Refresh } from "assets/icons/refresh-cw.svg";
import { useIndividualCompliance } from "contexts/individual-compliance-context";
import { IndividualComplianceQueryKeys } from "contexts/individual-compliance-context/consts";
import copy from "copy-to-clipboard";
import { IndividualContentItem } from "features/individual-compliance/selected-individual/IndividualContentItem";
import { useMutateData } from "hooks/useMutateData";
import { maskRandomDigits } from "utils/general/mask-data";

import { Button } from "ui/button";
import { customSuccessToast } from "ui/custom-toast";
import { Paragraph3 } from "ui/typography";

import { BvnVerificationResetModal } from "./BvnVerificationResetModal";
// import { BvnVerificationResetModal } from "./BvnVerificationResetModal";
import { ComplianceStatus } from "./ComplianceStatus";
import { IIndividualContentItem, IndividualDetailsProps } from "./types";

export const BvnAndNinInformation = (props: IndividualDetailsProps) => {
  const {
    isContentWrapperBelow600,
    isContentWrapperBelow745,
    individual,
    hideResetBvnButton,
  } = props;
  const { setSelectedIndividualDetails } = useIndividualCompliance();
  const [isBvnResetModalOpen, setIsBvnResetModalOpen] = useState(false);

  const copyText = (text: string) => {
    if (text === "" || text === "N/A") return;
    copy(text);
    customSuccessToast({ message: `Copied: ${text}` });
  };

  const {
    bvn,
    bvnReference,
    dob,
    nin,
    kycDocumentType,
    kycJobId,
    kycVerifyDocument,
    kycResultText,
    kycJobStatus,
  } = individual;

  const userDetails: IIndividualContentItem[] = [
    {
      title: "Bank Verification Number",
      content: (
        <Box className="flex items-center">
          <p>{maskRandomDigits(bvn) || "N/A"}</p>
          {bvn && (
            <IconCopy
              onClick={() => copyText(bvn)}
              className="ml-1 cursor-pointer"
            />
          )}
        </Box>
      ),
    },
    {
      title: "Date of Birth",
      content: dob || "N/A",
    },
    {
      title: "BVN Reference",
      content: (
        <Box className="flex items-center">
          <p>{bvnReference || "N/A"}</p>
          {bvnReference && (
            <IconCopy
              onClick={() => copyText(bvnReference)}
              className="ml-1 cursor-pointer"
            />
          )}
        </Box>
      ),
    },
    {
      title: "National Identification Number",
      content: (
        <Box className="flex items-center">
          <p>{maskRandomDigits(nin) || "N/A"}</p>
          {nin && (
            <IconCopy
              onClick={() => copyText(nin)}
              className="ml-1 cursor-pointer"
            />
          )}
        </Box>
      ),
    },
    {
      title: "Document Type", //kycDocumentType
      content: kycDocumentType || "N/A",
    },
    {
      title: "Job ID",
      content: (
        <Box className="flex items-center">
          <p>{kycJobId}</p>
          {kycJobId ? (
            <IconCopy
              onClick={() => copyText(kycJobId)}
              className="ml-1 cursor-pointer"
            />
          ) : (
            "N/A"
          )}
        </Box>
      ),
    },
    {
      title: "Verify Document",
      content: kycVerifyDocument || "N/A",
    },
    {
      title: "Result Test",
      content: kycResultText || "N/A",
    },
    {
      title: "Job Status",
      content: (
        <ComplianceStatus
          status={kycJobStatus === "success" ? "approved" : "pending"}
          text={kycJobStatus === "success" ? "Success" : "pending"}
          className="w-fit"
        />
      ),
    },
  ];

  const {
    mutateAsync: resetBvnVerification,
    isPending: isResettingBvnVerification,
  } = useMutateData<IBvnVerificationPayload, IBvnVerificationResponse>(
    [IndividualComplianceQueryKeys.RESET_USER_BVN],
    {
      onSuccess: async (data) => {
        customSuccessToast({
          title: "Reset Bvn",
          message: data.message,
        });
        setSelectedIndividualDetails((previousSelectedIndividualDetails) =>
          previousSelectedIndividualDetails
            ? {
                ...previousSelectedIndividualDetails,
                kycJobStatus: null,
                kycLivenessCheck: null,
                kycJobId: null,
              }
            : null
        );
      },
      onError(error) {
        const err = error as unknown as IApiErrorData;
        const message = err.message;
        console.error(message);
        // customErrorToast({ title: "Individual Compliance", message });
      },
    }
  );

  const handleBvnResetSection = async () => {
    try {
      await resetBvnVerification({
        apiRoute: `/admin/user/reset-user-bvn`,
        data: {
          userId: individual.id,
        },
        method: "POST",
        module: "EARNIPAY_STG",
        useEncryption: true,
      });
    } catch (error) {
      console.error(error);
    }
    setIsBvnResetModalOpen(false);
  };

  return individual.id ? (
    <>
      <Box className="flex flex-col gap-6">
        <Box>
          <Box
            css={{
              display: "flex",
              flexWrap: "wrap",
              columnGap: "32px",
              rowGap: "16px",
            }}
          >
            {userDetails.map((verificatioResult, index) => (
              <IndividualContentItem
                key={index}
                title={verificatioResult.title}
                content={verificatioResult.content}
                isContentWrapperBelow745={isContentWrapperBelow745}
                isContentWrapperBelow600={isContentWrapperBelow600}
              />
            ))}
          </Box>
        </Box>
      </Box>
      <Box className="mt-4 flex items-center justify-end">
        {!hideResetBvnButton && (
          <Button
            variant="outline"
            textColor="#00535C"
            border={"1px solid #D0D5DD"}
            _hover={{
              border: "1px solid #D0D5DD",
            }}
            icon={<Refresh />}
            onClick={() => {
              setIsBvnResetModalOpen(true);
            }}
          >
            Reset
          </Button>
        )}
      </Box>
      <BvnVerificationResetModal
        handleBvnResetSection={handleBvnResetSection}
        handleToggleBvnResetModal={() =>
          setIsBvnResetModalOpen((prev) => !prev)
        }
        isLoading={isResettingBvnVerification}
        isOpen={isBvnResetModalOpen}
        setIsOpen={setIsBvnResetModalOpen}
      />
    </>
  ) : (
    <Box>
      <Paragraph3
        lineHeight="22.4px"
        textColor="#718096"
      >
        Nothing to see here yet.
      </Paragraph3>
    </Box>
  );
};
